import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60_000;
// Data older than this (no successful refresh) is shown as stale.
const STALE_AFTER_MS = 3 * POLL_MS;

/**
 * Real platform telemetry for the app chrome (status strip, classification
 * bar, ticker). One shared poller per endpoint, so several widgets on the
 * same page cost a single request.
 *
 * Store state: { data, error, loaded, updatedAt }
 *   data       last successful payload (kept when a later poll fails)
 *   error      false | 'network' (API unreachable) | 'rate_limited' | 'http'
 *   updatedAt  ms timestamp of the last success
 * A failed poll never turns into "feeds down": that only comes from the
 * server's own state. Use platformState() to derive what to show.
 */
const classify = (err) => {
  if (!err?.response) return 'network';
  if (err.response.status === 429) return 'rate_limited';
  return 'http';
};

const EMPTY = { data: null, error: false, loaded: false, updatedAt: 0 };

/**
 * `scopeOf` (optional) names whose data the store holds, e.g. the session
 * token. A snapshot taken under another scope is never handed out, so one
 * account's tailored data cannot leak to a visitor or the next account after
 * sign-out / sign-in on the same browser.
 */
function createStore(fetcher, scopeOf = () => null) {
  let state = EMPTY;
  let scope = scopeOf();
  const listeners = new Set();
  let timer = null;

  const current = () => (scope === scopeOf() ? state : EMPTY);
  const emit = () => listeners.forEach((l) => l(current()));

  const run = async () => {
    const runScope = scopeOf();
    try {
      const data = await fetcher();
      if (runScope !== scopeOf()) return; // session changed mid-request
      state = { data, error: false, loaded: true, updatedAt: Date.now() };
    } catch (err) {
      if (runScope !== scopeOf()) return;
      // Keep the last good payload only when it belongs to this same scope.
      const base = runScope === scope ? state : EMPTY;
      state = { ...base, error: classify(err), loaded: true };
    }
    scope = runScope;
    emit();
  };

  return {
    getState: current,
    subscribe(listener) {
      listeners.add(listener);
      listener(current());
      if (!timer) {
        run();
        timer = setInterval(run, POLL_MS);
      } else if (scope !== scopeOf()) {
        run();
      }
      return () => {
        listeners.delete(listener);
        if (!listeners.size) {
          clearInterval(timer);
          timer = null;
        }
      };
    },
    refresh: run,
  };
}

const readToken = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const authHeader = (key) => {
  const t = readToken(key);
  return t ? { Authorization: `Bearer ${t}` } : null;
};

const stores = {
  platform: createStore(async () => (await axios.get(`${API}/system/status`)).data),
  admin: createStore(async () => {
    const headers = authHeader('admin_token');
    if (!headers) throw new Error('no admin session');
    return (await axios.get(`${API}/admin/system/status`, { headers })).data;
  }),
  headlines: createStore(async () => {
    const headers = authHeader('token');
    // Signed-in users see their own tailored stories; visitors see public OSINT headlines.
    if (headers) {
      const res = await axios.get(`${API}/dashboard/attacks?severity=all&limit=10`, { headers });
      return Array.isArray(res.data) ? res.data : [];
    }
    return (await axios.get(`${API}/public/headlines?limit=10`)).data;
  }, () => readToken('token')),
};

// Seeded from the store's current snapshot so a remount (route change) starts
// from the last known values instead of flashing back to "checking".
// `enabled: false` neither subscribes nor polls. The store is re-checked on
// render so a still-mounted widget drops another session's data at once.
function useStore(name, enabled = true) {
  const store = stores[name];
  const [state, setState] = useState(() => (enabled ? store.getState() : EMPTY));
  useEffect(() => (enabled ? store.subscribe(setState) : undefined), [store, enabled]);
  const shown = enabled && store.getState() !== EMPTY ? state : EMPTY;
  return { ...shown, refresh: store.refresh };
}

export const usePlatformStatus = () => useStore('platform');
export const useAdminStatus = (enabled = true) => useStore('admin', enabled);
export const useHeadlines = () => useStore('headlines');

/** True when this browser holds an admin console session. */
export const hasAdminSession = () => {
  try {
    return Boolean(localStorage.getItem('admin_token'));
  } catch {
    return false;
  }
};

/**
 * What the chrome should say about the platform.
 *   'operational' | 'degraded' | 'down' | 'starting'  server-reported feed state
 *   'unreachable'  the API itself did not answer and we have nothing cached
 *   'unknown'      the API answered with an error and we have nothing cached
 *   'checking'     first request still in flight
 * `stale` is true when the shown data is from an earlier successful poll.
 *
 * "Feeds healthy" is only claimed when most checked sources are up: the
 * server reports 'operational' as soon as any one source answers, so a
 * large share of failing sources is shown as degraded here.
 */
const HEALTHY_SHARE = 0.8;

export function platformState({ data, error, loaded, updatedAt }) {
  if (data?.state) {
    const stale = Boolean(error) && Date.now() - (updatedAt || 0) > STALE_AFTER_MS;
    let state = data.state;
    const checked = data.sources_checked;
    if (state === 'operational' && checked > 0 && data.sources_up < HEALTHY_SHARE * checked) {
      state = 'degraded';
    }
    return { state, stale };
  }
  if (!loaded) return { state: 'checking', stale: false };
  if (error === 'network') return { state: 'unreachable', stale: false };
  return { state: 'unknown', stale: false };
}

/**
 * Text for a visually hidden polite live region. Empty until the platform
 * state actually changes after the first known value, so screen readers hear
 * "Feeds degraded" when it happens, not a status readout on every page load.
 */
export function useStateAnnouncement(state) {
  const seenRef = useRef(false);
  const [message, setMessage] = useState('');
  useEffect(() => {
    if (state === 'checking') return;
    if (!seenRef.current) {
      seenRef.current = true;
      return;
    }
    setMessage(`Platform status: ${STATE_TEXT[state]?.[0] || state}`);
  }, [state]);
  return message;
}

/** "4m ago" style label for an ISO timestamp. */
export function timeAgo(iso) {
  if (!iso) return '-';
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export const STATE_LABEL = {
  operational: 'OPERATIONAL',
  degraded: 'DEGRADED',
  down: 'FEEDS DOWN',
  starting: 'STARTING',
  unreachable: 'API UNREACHABLE',
  unknown: 'STATUS UNKNOWN',
  checking: 'CHECKING',
};

/** Sentence-case versions plus a one-line explanation for tooltips / SR text. */
export const STATE_TEXT = {
  operational: ['Feeds healthy', 'Sources are returning content and ingestion is current.'],
  degraded: ['Feeds degraded', 'Some sources failed recently or ingestion is behind.'],
  down: ['Feeds down', 'No source has returned content recently and ingestion has stopped.'],
  starting: ['Starting', 'No feed check has completed yet.'],
  unreachable: ['API unreachable', 'The IntelliSecure API did not respond. Feeds may be fine; this browser cannot reach the server.'],
  unknown: ['Status unknown', 'The status endpoint returned an error.'],
  checking: ['Checking status', 'Contacting the API.'],
};
